import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BackButton from '../components/BackButton';
import { useFeedback } from '../components/FeedbackProvider';
import { botService } from '../services';
import '../styles/App.css';

const formatTokenCount = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed.toLocaleString() : '0';
};

const parseTokenInput = (value, { allowZero = true } = {}) => {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 0 || (!allowZero && parsed <= 0)) {
    return null;
  }
  return parsed;
};

const providerLabel = (provider) => ({
  deepseek: 'DeepSeek',
  openai: 'OpenAI',
  gemini: 'Gemini',
  claude: 'Claude',
  chatglm: 'ChatGLM'
}[provider] || provider || 'DeepSeek');

const getTokenNumber = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
};

const getUsedPercent = (quota) => {
  const remaining = getTokenNumber(quota.remaining_tokens);
  const used = getTokenNumber(quota.used_tokens);
  const total = remaining + used;
  return total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
};

const AiTokenQuotaAdmin = () => {
  const { success: notifySuccess, error: notifyError } = useFeedback();
  const [loading, setLoading] = useState(true);
  const [savingRoomUid, setSavingRoomUid] = useState('');
  const [quotas, setQuotas] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [error, setError] = useState('');

  const loadQuotas = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await botService.listRoomAiTokenQuotas();
      setQuotas(result.quotas || []);
    } catch (err) {
      const message = err.response?.data?.message || '无法读取 AI token 额度';
      setError(message);
      notifyError(message);
    } finally {
      setLoading(false);
    }
  }, [notifyError]);

  useEffect(() => {
    loadQuotas();
  }, [loadQuotas]);

  const stats = useMemo(() => quotas.reduce((acc, quota) => {
    acc.rooms += 1;
    if (quota.has_room_key) {
      acc.roomKeys += 1;
    } else {
      acc.defaultApi += 1;
    }
    acc.remaining += getTokenNumber(quota.remaining_tokens);
    acc.used += getTokenNumber(quota.used_tokens);
    return acc;
  }, {
    rooms: 0,
    defaultApi: 0,
    roomKeys: 0,
    remaining: 0,
    used: 0
  }), [quotas]);

  const updateDraft = (roomUid, field, value) => {
    setDrafts((current) => ({
      ...current,
      [roomUid]: {
        ...(current[roomUid] || {}),
        [field]: value
      }
    }));
  };

  const refreshOne = (quota) => {
    if (!quota?.room_uid) return;
    setQuotas((current) => current.map((item) => (
      String(item.room_uid) === String(quota.room_uid)
        ? { ...item, ...quota }
        : item
    )));
  };

  const handleAdd = async (roomUid) => {
    const tokens = parseTokenInput(drafts[roomUid]?.add, { allowZero: false });
    if (tokens === null) {
      notifyError('追加额度必须为正整数');
      return;
    }
    setSavingRoomUid(roomUid);
    try {
      const result = await botService.addRoomAiTokenQuota(roomUid, tokens);
      refreshOne(result.quota);
      updateDraft(roomUid, 'add', '');
      notifySuccess('额度已追加');
    } catch (err) {
      notifyError(err.response?.data?.message || '追加额度失败');
    } finally {
      setSavingRoomUid('');
    }
  };

  const handleSet = async (roomUid) => {
    const remainingTokens = parseTokenInput(drafts[roomUid]?.set, { allowZero: true });
    if (remainingTokens === null) {
      notifyError('剩余额度必须为非负整数');
      return;
    }
    setSavingRoomUid(roomUid);
    try {
      const result = await botService.setRoomAiTokenQuota(roomUid, remainingTokens);
      refreshOne(result.quota);
      updateDraft(roomUid, 'set', '');
      notifySuccess('额度已设置');
    } catch (err) {
      notifyError(err.response?.data?.message || '设置额度失败');
    } finally {
      setSavingRoomUid('');
    }
  };

  return (
    <div className="ai-token-admin">
      <BackButton to="/admin/bot" />

      <div className="ai-token-admin-header">
        <div>
          <span className="ai-token-admin-kicker">Bot Admin</span>
          <h1>AI Token 额度管理</h1>
          <p>默认 API 额度只在房间未配置自带 Key 时消耗；房间自带 Key 不占用默认额度。</p>
        </div>
        <button className="secondary-action ai-token-refresh" type="button" onClick={loadQuotas} disabled={loading}>
          刷新
        </button>
      </div>

      {error && <div className="ai-token-alert ai-token-alert-error">{error}</div>}

      <div className="ai-token-metrics" aria-label="AI Token 额度概览">
        <div>
          <span>房间数</span>
          <strong>{formatTokenCount(stats.rooms)}</strong>
        </div>
        <div>
          <span>默认 API 房间</span>
          <strong>{formatTokenCount(stats.defaultApi)}</strong>
        </div>
        <div>
          <span>自带 Key 房间</span>
          <strong>{formatTokenCount(stats.roomKeys)}</strong>
        </div>
        <div>
          <span>剩余总额度</span>
          <strong>{formatTokenCount(stats.remaining)}</strong>
        </div>
        <div>
          <span>已使用总量</span>
          <strong>{formatTokenCount(stats.used)}</strong>
        </div>
      </div>

      <section className="ai-token-panel">
        <div className="ai-token-panel-header">
          <div>
            <h2>房间额度</h2>
            <span>只列出当前 bot 配置里的房间</span>
          </div>
        </div>

        {loading ? (
          <div className="ai-token-state">加载中...</div>
        ) : quotas.length === 0 ? (
          <div className="ai-token-state">当前 bot 配置中没有房间</div>
        ) : (
          <div className="ai-token-table-wrap">
            <table className="ai-token-table">
              <thead>
                <tr>
                  <th>房间</th>
                  <th>使用方式</th>
                  <th>额度</th>
                  <th>调整额度</th>
                </tr>
              </thead>
              <tbody>
                {quotas.map((quota) => {
                  const roomUid = String(quota.room_uid || '');
                  const saving = savingRoomUid === roomUid;
                  const usedPercent = getUsedPercent(quota);
                  const roomMeta = [
                    roomUid ? `UID ${roomUid}` : '',
                    quota.room_id ? `房间号 ${quota.room_id}` : ''
                  ].filter(Boolean).join(' / ');

                  return (
                    <tr key={roomUid}>
                      <td data-label="房间">
                        <div className="ai-token-room">
                          <strong>{quota.room_name || roomUid || '未命名房间'}</strong>
                          <span>{roomMeta || '暂无房间标识'}</span>
                        </div>
                      </td>
                      <td data-label="使用方式">
                        <div className="ai-token-usage-source">
                          <span className="ai-token-provider">{providerLabel(quota.provider)}</span>
                          <span className={`ai-token-badge ${quota.has_room_key ? 'room-key' : 'default-api'}`}>
                            {quota.has_room_key ? '房间 Key' : '默认 API'}
                          </span>
                          <small>{quota.has_room_key ? '不消耗默认额度' : '消耗默认额度'}</small>
                        </div>
                      </td>
                      <td data-label="额度">
                        <div className="ai-token-quota-cell">
                          <div className="ai-token-number-row">
                            <span>剩余</span>
                            <strong>{formatTokenCount(quota.remaining_tokens)}</strong>
                          </div>
                          <div className="ai-token-number-row muted">
                            <span>已用</span>
                            <strong>{formatTokenCount(quota.used_tokens)}</strong>
                          </div>
                          <div className="ai-token-progress" aria-label={`已使用 ${usedPercent}%`}>
                            <span style={{ width: `${usedPercent}%` }} />
                          </div>
                        </div>
                      </td>
                      <td data-label="调整额度">
                        <div className="ai-token-actions">
                          <label>
                            <span>追加</span>
                            <div>
                              <input
                                type="number"
                                min="1"
                                value={drafts[roomUid]?.add || ''}
                                onChange={(e) => updateDraft(roomUid, 'add', e.target.value)}
                                placeholder="token"
                              />
                              <button className="primary-action" type="button" onClick={() => handleAdd(roomUid)} disabled={saving}>
                                追加
                              </button>
                            </div>
                          </label>
                          <label>
                            <span>设置余额</span>
                            <div>
                              <input
                                type="number"
                                min="0"
                                value={drafts[roomUid]?.set || ''}
                                onChange={(e) => updateDraft(roomUid, 'set', e.target.value)}
                                placeholder="剩余 token"
                              />
                              <button className="secondary-action" type="button" onClick={() => handleSet(roomUid)} disabled={saving}>
                                设置
                              </button>
                            </div>
                          </label>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default AiTokenQuotaAdmin;
